/**
 * GlobalAudioPlayer.tsx — el `<audio>` real, invisible, único en toda la app.
 *
 * Vive en el `MainLayout`. No renderiza UI visible — el único control humano
 * es el `<AudioFloatingButton>` que consume el mismo context. Toda la lógica
 * de reproducción se delega al motor HTML5 nativo (suficiente para audio
 * ambiental en loop, no necesitamos Web Audio API).
 *
 * Estrategia de autoplay (compatible con políticas del navegador):
 *  1. Al montar (o al cambiar `audioUrl`), aplicamos el `muted` actual del
 *     context y llamamos a `.play()` dentro de un `try/catch`.
 *  2. Si el navegador rechaza con `NotAllowedError` (Chrome/Safari/Firefox
 *     bloquean autoplay CON sonido sin gesto previo):
 *       · Auto-mute vía `setMuted(true)` — así el próximo intento pasa.
 *       · Reintentamos `.play()` muteado.
 *       · Emitimos un toast informativo UNA sola vez por sesión:
 *         "Haz clic en el ícono de audio para escuchar la música de fondo".
 *       · Marcamos `needsUserGesture=true` para que el FAB pueda destacar.
 *  3. Cuando `audioUrl` vuelve a `null`, pausamos y limpiamos.
 *
 * Sync bidireccional:
 *  · Cambios de `isMuted` en context → aplican al `<audio>.muted`.
 *  · Eventos `play`/`pause`/`ended` del `<audio>` → sync a `markPlaying`.
 */
import { useEffect, useRef } from 'react';

import { useToast } from '../../components/ui/ToastProvider';
import { useAudioPlayer } from './AudioPlayerContext';

/** Flag de módulo: garantiza que el toast informativo aparece 1 vez por sesión.
 *  Nivel módulo (no state) para que sobreviva re-renders del componente. */
let autoplayToastShownThisSession = false;

export function GlobalAudioPlayer() {
  const { audioUrl, isMuted, setMuted, markPlaying, markNeedsUserGesture } = useAudioPlayer();
  const { toast } = useToast();

  const audioRef = useRef<HTMLAudioElement | null>(null);

  // -------------------------------------------------------------------------
  // Volumen default agradable al montar. Se aplica una sola vez porque
  // luego el `<audio>` recuerda su propio volumen entre cambios de `src`.
  // El valor 0.4 es intencionalmente bajo — es AMBIENTAL, no de foreground.
  // -------------------------------------------------------------------------
  useEffect(() => {
    const el = audioRef.current;
    if (el) el.volume = 0.4;
  }, []);

  // -------------------------------------------------------------------------
  // Sync mute → <audio>.muted + play imperativo al desmutar.
  //
  // Bug QA 2026-06-26: el effect previo SÓLO seteaba `el.muted = isMuted`
  // y confiaba en que el audio ya estaba sonando. Si el autoplay inicial
  // falló (política del browser, error de red, primer load), el `<audio>`
  // quedaba `paused=true` y al desmutar SÓLO abríamos el volumen sobre
  // silencio absoluto — el usuario oía nada. Ahora al desmutar disparamos
  // `.play()` explícitamente con `.catch()` para cubrir ese caso, y usamos
  // el flag `needsUserGesture` para señalar al FAB si el browser sigue
  // bloqueando.
  // -------------------------------------------------------------------------
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    // Sync inmediato del atributo — cubre todos los renders.
    el.muted = isMuted;
    // Sin URL activa, no hay nada que reproducir aunque el usuario destile.
    if (!audioUrl) return;
    if (!isMuted) {
      // Prescripción del ticket: play imperativo con catch. Idempotente si
      // el audio ya estaba sonando (spec HTML5: `play()` en un elemento
      // ya en playing resuelve inmediatamente).
      void el
        .play()
        .then(() => markNeedsUserGesture(false))
        .catch((err) => {
          console.warn('[audio] Autoplay bloqueado', err);
          markNeedsUserGesture(true);
        });
    }
    // Nota: cuando `isMuted=true` intencionalmente NO llamamos `pause()`.
    // Mantenemos el `<audio>` sonando muteado para que la transición
    // desmutear → escuchar sea instantánea y continua (sin cortes ni
    // reinicio del loop). El ticket permite ambas opciones ("pause() O
    // muted=true"); elegimos muted=true por UX más suave.
  }, [isMuted, audioUrl, markNeedsUserGesture]);

  // -------------------------------------------------------------------------
  // Listeners del <audio>: mantienen `isPlaying` del context sincronizado.
  // -------------------------------------------------------------------------
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    const onPlay = () => markPlaying(true);
    const onPause = () => markPlaying(false);
    const onEnded = () => markPlaying(false);

    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onEnded);
    return () => {
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onEnded);
    };
  }, [markPlaying]);

  // -------------------------------------------------------------------------
  // Reaccionar a cambios de `audioUrl`: cargar + intentar autoplay.
  // -------------------------------------------------------------------------
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    // Sin URL → pausar y limpiar. No hay nada más que hacer.
    if (!audioUrl) {
      el.pause();
      el.removeAttribute('src');
      el.load();
      return;
    }

    // Cargar el nuevo src en el elemento nativo. Sólo si cambió — evita
    // re-fetch innecesario si el mismo componente re-inyecta la misma URL
    // (React StrictMode dispara effects dos veces en dev).
    if (el.src !== audioUrl) {
      el.src = audioUrl;
      el.load();
    }

    // Aseguramos que el estado nativo refleje `isMuted` antes del play.
    el.muted = isMuted;

    // Intento primario: play respetando la preferencia mute actual.
    const attemptPlay = async (): Promise<void> => {
      try {
        await el.play();
        markNeedsUserGesture(false);
      } catch (err) {
        // NotAllowedError (autoplay policy): auto-mute + reintento + toast.
        // Cualquier otro error de carga (AbortError, NotSupportedError) también
        // cae aquí — es más seguro pausar y avisar que dejar el player colgado.
        const isNotAllowed = err instanceof DOMException && err.name === 'NotAllowedError';

        if (isNotAllowed) {
          // Auto-mute: cambia el context, el effect de arriba aplica al <audio>.
          setMuted(true);
          markNeedsUserGesture(true);

          // Reintento muteado. La mayoría de browsers acepta autoplay muted.
          try {
            el.muted = true;
            await el.play();
          } catch {
            // Aún así falló — dejamos el flag activo para que el FAB destaque.
          }

          if (!autoplayToastShownThisSession) {
            autoplayToastShownThisSession = true;
            toast({
              variant: 'info',
              title: 'Música de fondo lista',
              description: 'Haz clic en el ícono de audio para escuchar la música de fondo.',
            });
          }
        }
      }
    };

    void attemptPlay();
    // División de responsabilidades entre los dos effects que llaman a play():
    //  · ESTE (`[audioUrl]`) — maneja la CARGA inicial + autoplay muted con
    //    fallback NotAllowedError → auto-mute → toast. Corre 1 vez por URL.
    //  · El de arriba (`[isMuted, audioUrl]`) — maneja cambios de mute por
    //    click del FAB: desmuta y llama play() imperativo por si el audio
    //    estaba pausado (autoplay que falló). No se solapan porque cuando
    //    `audioUrl` cambia, el sync effect corre primero con isMuted=default
    //    (típicamente true → no play), y luego este effect toma la carga.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioUrl]);

  // -------------------------------------------------------------------------
  // Cleanup al desmontar el layout (caso raro pero cubre HMR y unmount total).
  // -------------------------------------------------------------------------
  useEffect(() => {
    const el = audioRef.current;
    return () => {
      if (el) {
        el.pause();
        el.removeAttribute('src');
      }
    };
  }, []);

  return (
    // A11y: audio ambiental sin diálogo; no requiere <track> de captions.
    <audio
      ref={audioRef}
      // `src` reactivo — cuando cambia, el effect de arriba corre y hace .play().
      // No usamos `src={audioUrl ?? undefined}` para evitar que React recree
      // el elemento cuando la URL vuelve a null; en su lugar el effect limpia
      // con removeAttribute('src').
      preload="metadata"
      loop
      playsInline
      aria-hidden="true"
      // Handler de error: logueamos con contexto para descartar problemas
      // de CORS con Supabase Storage o URLs externas caídas. El browser
      // dispara `error` tras 3 reintentos fallidos de decoding/network.
      // No mostramos toast — un audio ambiental que falla en cargar no
      // amerita interrumpir la UX; la ausencia de sonido ya es feedback.
      onError={(e) => {
        const el = e.currentTarget;
        // `MediaError.code`: 1 ABORTED · 2 NETWORK · 3 DECODE · 4 SRC_NOT_SUPPORTED.
        console.error('[audio] Error cargando audio:', {
          src: el.currentSrc || el.src,
          errorCode: el.error?.code,
          errorMessage: el.error?.message,
        });
      }}
      // `style` inline para blindar contra cualquier CSS global que pudiera
      // hacer visible el elemento. Alternativa a `sr-only` que no aplica a
      // <audio> sin controls.
      style={{ display: 'none' }}
    />
  );
}
